/**
 * Permission guards for NeoTIM route protection
 *
 * 3-layer architecture:
 *   Layer 1 — Resolution:  Where do permissions come from?
 *   Layer 2 — Checking:    Pure boolean logic over permission sets
 *   Layer 3 — Guards:      Route-level redirect-or-pass guards
 *
 * AuthProvider guarantees permissions are loaded before Router mounts.
 * Parent-loader → store fallback chain kept as defense-in-depth.
 */

import { redirect } from "@tanstack/react-router";
import { usePermissionStore } from "../stores/permissions.store";
import { matchesPermission } from "./parser";
import type { PermissionContext } from "./types";

// ─────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────

/** Resolved permissions bundle from any source */
interface ResolvedPermissions {
  readonly permissions: readonly string[];
  readonly hasWildcard: boolean;
  readonly context: PermissionContext;
}

/** Context passed to route guards by TanStack Router */
interface GuardContext {
  readonly parentMatchPromise?: Promise<unknown>;
}

/** Options for route-level guards */
interface GuardOptions {
  readonly redirectTo?: string;
}

// ─────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────

const UNAUTHORIZED_PATH = "/unauthorized";

const EMPTY_CONTEXT: PermissionContext = {
  filiale_code: "",
  dept_path: "",
  is_manager: false,
  is_dept_head: false,
  personne_id: "",
  managed_count: 0,
  accessible_filiales: [],
  profile_name: null,
  is_active: false,
};

// ─────────────────────────────────────────────
// Layer 1 — Resolution
// ─────────────────────────────────────────────

/**
 * Extract permissions from the parent route's loader data.
 * Returns null if the parent data is missing or malformed.
 */
async function getPermissionsFromParentLoader(
  parentMatchPromise: Promise<unknown>,
): Promise<ResolvedPermissions | null> {
  try {
    const parentMatch = await parentMatchPromise;
    if (!parentMatch || typeof parentMatch !== "object") return null;

    const parent = parentMatch as Record<string, unknown>;
    const loaderData = parent.loaderData as Record<string, unknown> | undefined;
    if (!loaderData || typeof loaderData !== "object") return null;
    if (!Array.isArray(loaderData.permissions)) return null;

    return {
      permissions: loaderData.permissions as readonly string[],
      hasWildcard: (loaderData.hasWildcard as boolean) ?? false,
      context: (loaderData.permissionContext as PermissionContext) ?? EMPTY_CONTEXT,
    };
  } catch {
    return null;
  }
}

/** Read permissions directly from the Zustand store. */
function getPermissionsFromStore(): ResolvedPermissions {
  const state = usePermissionStore.getState();
  return {
    permissions: state.permissions,
    hasWildcard: state.hasWildcard,
    context: state.context,
  };
}

/**
 * Resolve the current user's permissions.
 * Tries the parent route loader first, falls back to the Zustand store.
 *
 * AuthProvider populates the store before Router mounts, so the store
 * fallback is defense-in-depth, not the primary path.
 */
async function resolvePermissions(
  parentMatchPromise?: Promise<unknown>,
): Promise<ResolvedPermissions> {
  if (parentMatchPromise) {
    const fromLoader = await getPermissionsFromParentLoader(parentMatchPromise);
    if (fromLoader && (fromLoader.permissions.length > 0 || fromLoader.hasWildcard)) {
      return fromLoader;
    }
  }

  return getPermissionsFromStore();
}

// ─────────────────────────────────────────────
// Layer 2 — Checking (pure boolean logic)
// ─────────────────────────────────────────────

/**
 * A required entry may be a MODULE PATTERN (`security.*`) instead of a concrete
 * code: "any permission of that module". It is a pattern, not a permission code —
 * PermissionCode.php never declares one, hence no PERMISSIONS.* constant (same
 * form as the documented RH_MODULE_WILDCARD in capabilities/rh.ts).
 *
 * matchesPermission() is deliberately NOT used for it: on the required side a
 * pattern asks for module MEMBERSHIP (does the grant intersect the module, e.g.
 * `security.profiles.view` satisfies `security.*`), while capabilities rely on
 * `rh.*` keeping its grant-side coverage reading (hasFullRhAccess). The guard
 * layer owns module membership; the parser keeps its coverage semantics.
 */
function isModulePattern(required: string): boolean {
  return required.endsWith(".*");
}

/** Does any grant belong to the module (or cover it entirely)? */
function hasModuleGrant(permissions: readonly string[], module: string): boolean {
  return permissions.some((grant) => {
    if (grant === "*") return true;
    const code = grant.split(":")[0];
    return code === module || code.startsWith(`${module}.`);
  });
}

/**
 * Match a single required entry (concrete code or module pattern) against the
 * user's grants. Wildcard-holders short-circuit before this is reached.
 */
function matchesRequiredEntry(
  permissions: readonly string[],
  context: PermissionContext,
  required: string,
): boolean {
  if (isModulePattern(required)) {
    return hasModuleGrant(permissions, required.slice(0, -2));
  }
  return permissions.some((grant) => matchesPermission(grant, required, context));
}

/** Check if a single required permission is satisfied by the user's grants. */
function hasPermission(
  permissions: readonly string[],
  hasWildcard: boolean,
  context: PermissionContext,
  required: string,
): boolean {
  if (hasWildcard) return true;
  return matchesRequiredEntry(permissions, context, required);
}

/** Check if any of the required permissions is satisfied. */
function hasAnyPermission(
  permissions: readonly string[],
  hasWildcard: boolean,
  context: PermissionContext,
  required: string[],
): boolean {
  if (hasWildcard) return true;
  return required.some((entry) => matchesRequiredEntry(permissions, context, entry));
}

/** Check if all of the required permissions are satisfied. */
function hasAllPermissions(
  permissions: readonly string[],
  hasWildcard: boolean,
  context: PermissionContext,
  required: string[],
): boolean {
  if (hasWildcard) return true;
  return required.every((entry) => matchesRequiredEntry(permissions, context, entry));
}

/**
 * Check if user has HR management-level access.
 *
 * Filters out scope:self grants (plain employee level) and checks
 * for any rh.* permission among the remaining grants.
 * Wildcard users always pass.
 */
function hasHRManagementAccess(permissions: readonly string[], hasWildcard: boolean): boolean {
  if (hasWildcard) return true;
  return permissions.filter((p) => !p.includes(":scope:self")).some((p) => p.startsWith("rh."));
}

// ─────────────────────────────────────────────
// Layer 3 — Guards (route-level redirect-or-pass)
// ─────────────────────────────────────────────

/** Redirect to the specified path, or /unauthorized by default. */
function throwRedirect(path?: string): never {
  throw redirect({ to: path ?? UNAUTHORIZED_PATH });
}

/** Create a route guard that checks a single permission. */
function requirePermission(
  permission: string,
  options?: GuardOptions,
): (ctx: GuardContext) => Promise<void> {
  return async (ctx) => {
    const resolved = await resolvePermissions(ctx.parentMatchPromise);

    if (!hasPermission(resolved.permissions, resolved.hasWildcard, resolved.context, permission)) {
      throwRedirect(options?.redirectTo);
    }
  };
}

/** Create a route guard that checks any of the listed permissions. */
function requireAnyPermission(
  permissions: string[],
  options?: GuardOptions,
): (ctx: GuardContext) => Promise<void> {
  return async (ctx) => {
    const resolved = await resolvePermissions(ctx.parentMatchPromise);

    if (
      !hasAnyPermission(resolved.permissions, resolved.hasWildcard, resolved.context, permissions)
    ) {
      throwRedirect(options?.redirectTo);
    }
  };
}

/** Create a route guard that checks all of the listed permissions. */
function requireAllPermissions(
  permissions: string[],
  options?: GuardOptions,
): (ctx: GuardContext) => Promise<void> {
  return async (ctx) => {
    const resolved = await resolvePermissions(ctx.parentMatchPromise);

    if (
      !hasAllPermissions(resolved.permissions, resolved.hasWildcard, resolved.context, permissions)
    ) {
      throwRedirect(options?.redirectTo);
    }
  };
}

/**
 * Create a route guard for HR management-level access.
 * Users with only scope:self grants (plain employees) are redirected.
 */
function requireHRManagementAccess(options?: GuardOptions): (ctx: GuardContext) => Promise<void> {
  return async (ctx) => {
    const resolved = await resolvePermissions(ctx.parentMatchPromise);

    if (!hasHRManagementAccess(resolved.permissions, resolved.hasWildcard)) {
      throwRedirect(options?.redirectTo);
    }
  };
}

// ─────────────────────────────────────────────
// Exports
// ─────────────────────────────────────────────

// Layer 1 — Resolution
// Layer 2 — Checking
// Layer 3 — Guards
export {
  hasAllPermissions,
  hasAnyPermission,
  hasHRManagementAccess,
  hasPermission,
  requireAllPermissions,
  requireAnyPermission,
  requireHRManagementAccess,
  requirePermission,
  resolvePermissions,
};
